import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { FiHome } from 'react-icons/fi';
import { DotsSixVertical, Plus, Trash } from 'phosphor-react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import toast from 'react-hot-toast';

import api from '../../../services/api';

import Breadcrumbs from '../../../components/Breadcrumbs';
import LoaderBIO from '../../../components/Loader';
import Input from '../../../components/Form/Input';
import SimpleMultiSelect from '../../../components/Form/SimpleMultiSelect';
import MessageErrorValidator from '../../../components/MessageErrorValidator';
import WysiwygEditor from '../../../components/WysiwygEditor';
import Button from '../../../components/Button';

import { IExercisesProps, OptionsEntity } from '../../../interfaces/exercise';
import { ILessonProps } from '../../../interfaces/Lesson';

import {
  AddExerciseCard,
  CardQuestion,
  Container,
  EmptyState,
  ExerciseNavigation,
  HeaderSection,
  LineButtons,
  LineCorrectOption,
  LineField,
  LineInfoQuestions,
  SectionContainer,
  Separator,
} from './styles';

const exerciseSchema = Joi.object({
  id: Joi.string().allow('', null),
  lesson_id: Joi.string().allow('', null),
  exam: Joi.string()
    .required()
    .messages({ '*': 'Informe uma Instituição válida.' }),
  year: Joi.string().required().messages({ '*': 'Informe um ano válido.' }),
  difficulty: Joi.string()
    .required()
    .messages({ '*': 'Informe a dificuldade da questão.' }),
  content: Joi.string().max(3000).required().messages({
    '*': 'Informe um enunciado válido.',
    'string.max': `O campo enunciado deve ter no máximo {#limit} caracteres`,
  }),
  options: Joi.array().items(
    Joi.object({
      id: Joi.string().allow('', null),
      exercise_id: Joi.string().allow('', null),
      content: Joi.string().max(3000).required().messages({
        '*': 'Informe uma opção válida.',
        'string.max': `O campo opção deve ter no máximo {#limit} caracteres`,
      }),
    }),
  ),
  correctOption: Joi.string()
    .required()
    .messages({ '*': 'Informe uma resposta correta.' }),
  resolution: Joi.string().max(3000).allow('').messages({
    '*': 'Informe uma resolução válida.',
    'string.max': `O campo resolução deve ter no máximo {#limit} caracteres`,
  }),
});

const EMPTY_OPTION = { id: '', exercise_id: '', content: '' };

const buildEmptyExercise = (lessonId: string): IExercisesProps => ({
  id: '',
  lesson_id: lessonId,
  exam: '',
  year: '',
  difficulty: 'easy',
  content: '',
  resolution: '',
  total_hits: 0,
  total_misses: 0,
  hit_percentage: 0,
  created_at: '',
  updated_at: '',
  options: Array(5)
    .fill(null)
    .map(() => ({
      id: '',
      exercise_id: '',
      content: '',
      is_correct: false,
      total_selected: 0,
      created_at: '',
      updated_at: '',
    })),
});

const ExerciciosEdit: React.FC = () => {
  const { clusterSlug, id } = useParams<{ clusterSlug: string; id: string }>();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exercises, setExercises] = useState<IExercisesProps[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [lessonData, setLessonData] = useState<ILessonProps>(
    {} as ILessonProps,
  );

  const isLoadingExercise = useRef(false);
  const cleanValues = useRef<{
    content: string;
    resolution: string;
    options: string[];
    difficulty: string;
    correctOption: string;
  }>({ content: '', resolution: '', options: ['', '', '', '', ''], difficulty: '', correctOption: '' });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(exerciseSchema),
  });

  const loadExerciseIntoForm = useCallback(
    (exercise: IExercisesProps) => {
      isLoadingExercise.current = true;

      const content = exercise.content || '';
      const resolution = exercise.resolution || '';
      const difficulty = exercise.difficulty || 'easy';
      const correctOption = exercise.options?.length
        ? String(exercise.options.findIndex(o => o.is_correct))
        : '';
      const optionsContent = exercise.options?.length
        ? exercise.options.map(o => o.content || '')
        : ['', '', '', '', ''];

      cleanValues.current = { content, resolution, difficulty, correctOption, options: optionsContent };

      reset({
        id: exercise.id || '',
        lesson_id: exercise.lesson_id || id,
        exam: exercise.exam || '',
        year: exercise.year || '',
        difficulty,
        content,
        correctOption,
        resolution,
        options: exercise.options?.length
          ? exercise.options.map(o => ({
              id: o.id || '',
              exercise_id: o.exercise_id || '',
              content: o.content || '',
            }))
          : Array(5).fill(EMPTY_OPTION),
      });

      setHasChanges(false);
      setFormKey(k => k + 1);

      setTimeout(() => {
        isLoadingExercise.current = false;
      }, 200);
    },
    [id, reset],
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/clusters/${clusterSlug}/lessons/${id}`,
        );
        setLessonData(response.data);

        const exercisesData: IExercisesProps[] = response.data.exercises || [];
        setExercises(exercisesData);

        if (exercisesData.length > 0) {
          loadExerciseIntoForm(exercisesData[0]);
        }
      } catch (error) {
        console.error(error);
        toast.error('Erro ao carregar exercícios.');
      } finally {
        setLoading(false);
      }
    })();
  }, [clusterSlug, id, loadExerciseIntoForm]);

  const handleNavigateToExercise = useCallback(
    (index: number) => {
      if (index === currentIndex) return;

      if (hasChanges) {
        if (
          !window.confirm(
            'Você tem alterações não salvas. Deseja descartá-las e navegar para outro exercício?',
          )
        ) {
          return;
        }
      }

      setCurrentIndex(index);
      loadExerciseIntoForm(exercises[index]);
    },
    [currentIndex, hasChanges, exercises, loadExerciseIntoForm],
  );

  const markDirty = useCallback(() => {
    if (!isLoadingExercise.current) {
      setHasChanges(true);
    }
  }, []);

  const handleDifficultyChange = useCallback(
    (values: string) => {
      setValue('difficulty', values);
      if (!isLoadingExercise.current && values !== cleanValues.current.difficulty) {
        setHasChanges(true);
      }
    },
    [setValue],
  );

  const handleCorrectOptionChange = useCallback(
    (values: string) => {
      setValue('correctOption', values);
      if (!isLoadingExercise.current && values !== cleanValues.current.correctOption) {
        setHasChanges(true);
      }
    },
    [setValue],
  );

  const handleContentChange = useCallback(
    (values: string) => {
      setValue('content', values);
      if (!isLoadingExercise.current && values !== cleanValues.current.content) {
        setHasChanges(true);
      }
    },
    [setValue],
  );

  const handleResolutionChange = useCallback(
    (values: string) => {
      setValue('resolution', values);
      if (!isLoadingExercise.current && values !== cleanValues.current.resolution) {
        setHasChanges(true);
      }
    },
    [setValue],
  );

  const optionChangeHandlers = React.useMemo(
    () =>
      [0, 1, 2, 3, 4].map(idx => (values: string) => {
        setValue(`options[${idx}][content]` as any, values);
        if (!isLoadingExercise.current && values !== cleanValues.current.options[idx]) {
          setHasChanges(true);
        }
      }),
    [setValue],
  );

  const onSubmit = handleSubmit(async data => {
    try {
      setSaving(true);

      const exercise = exercises[currentIndex];
      const isNew = !exercise.id;

      const body = {
        content: data.content,
        resolution: data.resolution || null,
        year: data.year || null,
        difficulty: data.difficulty || null,
        exam: data.exam ? String(data.exam).toUpperCase() : null,
        options: data.options.map(
          (option: { id: string; content: string }, idx: number) => ({
            ...(option.id ? { id: option.id } : {}),
            content: option.content,
            is_correct: Number(data.correctOption) === idx,
          }),
        ),
      };

      let response;
      if (isNew) {
        response = await api.post(`/exercises`, { ...body, lesson_id: id });
      } else {
        response = await api.put(`/exercises/${exercise.id}`, body);
      }

      const updatedExercises = [...exercises];
      updatedExercises[currentIndex] = response.data;
      setExercises(updatedExercises);
      loadExerciseIntoForm(response.data);

      toast.success('Exercício salvo com sucesso!', {
        position: 'bottom-right',
        duration: 4000,
        className: 'toast-samuquinha',
      });
    } catch (error) {
      console.error(error);
      toast.error(
        'Erro ao salvar exercício. Verifique os campos e tente novamente.',
        {
          position: 'bottom-right',
          duration: 5000,
          className: 'toast-samuquinha',
        },
      );
    } finally {
      setSaving(false);
    }
  });

  const handleAddExercise = useCallback(() => {
    if (hasChanges) {
      if (
        !window.confirm('Você tem alterações não salvas. Deseja descartá-las?')
      ) {
        return;
      }
    }

    const newExercise = buildEmptyExercise(id);
    const newExercises = [...exercises, newExercise];
    const newIndex = newExercises.length - 1;

    setExercises(newExercises);
    setCurrentIndex(newIndex);
    loadExerciseIntoForm(newExercise);
  }, [hasChanges, exercises, id, loadExerciseIntoForm]);

  const handleRemoveExercise = useCallback(async () => {
    if (!window.confirm('Deseja realmente remover este exercício?')) return;

    const exercise = exercises[currentIndex];

    try {
      if (exercise.id) {
        await api.delete(`/exercises/${exercise.id}`);
      }

      const newExercises = exercises.filter((_, i) => i !== currentIndex);
      setExercises(newExercises);

      const newIndex = Math.max(0, currentIndex - 1);
      setCurrentIndex(newIndex);

      if (newExercises.length > 0) {
        loadExerciseIntoForm(newExercises[newIndex]);
      } else {
        reset({});
        setHasChanges(false);
      }

      toast.success('Exercício removido com sucesso!', {
        position: 'bottom-right',
        duration: 4000,
        className: 'toast-samuquinha',
      });
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover exercício.', {
        position: 'bottom-right',
        duration: 4000,
        className: 'toast-samuquinha',
      });
    }
  }, [exercises, currentIndex, loadExerciseIntoForm, reset]);

  const onDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      if (hasChanges) {
        if (
          !window.confirm(
            'Você tem alterações não salvas. Deseja descartá-las para reordenar?',
          )
        )
          return;
      }

      const reordered = Array.from(exercises);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);

      const newCurrentIndex = reordered.findIndex(
        e => e.id === exercises[currentIndex].id,
      );
      setExercises(reordered);
      setCurrentIndex(newCurrentIndex);
      loadExerciseIntoForm(reordered[newCurrentIndex]);

      try {
        await api.patch(`/exercises/reorder`, {
          lesson_id: id,
          exercises: reordered.map((e, idx) => ({ id: e.id, order: idx + 1 })),
        });
      } catch (error) {
        console.error(error);
        toast.error('Erro ao reordenar exercícios.', {
          position: 'bottom-right',
          duration: 4000,
          className: 'toast-samuquinha',
        });
      }
    },
    [hasChanges, exercises, currentIndex, id, loadExerciseIntoForm],
  );

  const onOptionDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      const exercise = exercises[currentIndex];
      if (!exercise.id) {
        toast.error('Salve o exercício antes de reordenar as alternativas.', {
          position: 'bottom-right',
          duration: 4000,
          className: 'toast-samuquinha',
        });
        return;
      }

      const currentValues = getValues();
      const src = result.source.index;
      const dest = result.destination.index;

      const reorderedOptions = Array.from(currentValues.options as any[]);
      const [moved] = reorderedOptions.splice(src, 1);
      reorderedOptions.splice(dest, 0, moved);

      const oldCorrectIdx = Number(currentValues.correctOption);
      let newCorrectIdx = oldCorrectIdx;
      if (oldCorrectIdx === src) {
        newCorrectIdx = dest;
      } else if (src < oldCorrectIdx && dest >= oldCorrectIdx) {
        newCorrectIdx = oldCorrectIdx - 1;
      } else if (src > oldCorrectIdx && dest <= oldCorrectIdx) {
        newCorrectIdx = oldCorrectIdx + 1;
      }

      const wasChanged = hasChanges;
      isLoadingExercise.current = true;

      // Sync clean option values to the new order so post-remount WysiwygEditor
      // fires don't incorrectly mark the form dirty.
      const reorderedClean = reorderedOptions.map((opt: any) => opt.content as string);
      cleanValues.current = {
        ...cleanValues.current,
        options: reorderedClean,
        correctOption: String(newCorrectIdx),
      };

      reset({
        ...currentValues,
        options: reorderedOptions,
        correctOption: String(newCorrectIdx),
      });
      setFormKey(k => k + 1);
      setTimeout(() => {
        isLoadingExercise.current = false;
        if (wasChanged) setHasChanges(true);
      }, 200);

      try {
        await api.patch(`/exercises/${exercise.id}/options/reorder`, {
          options: reorderedOptions.map((opt: { id: string }, idx: number) => ({
            id: opt.id,
            order: idx + 1,
          })),
        });
      } catch (error) {
        console.error(error);
        toast.error('Erro ao reordenar alternativas.', {
          position: 'bottom-right',
          duration: 4000,
          className: 'toast-samuquinha',
        });
      }
    },
    [hasChanges, exercises, currentIndex, getValues, reset],
  );

  return (
    <>
      <LoaderBIO isVisible={loading} />

      <Container>
        <HeaderSection>
          <Breadcrumbs
            icon={FiHome}
            separator="/"
            route={[
              { title: 'Módulos', link: '/modulos' },
              {
                title: `${lessonData?.cluster?.title || clusterSlug}`,
                link: `/aulas/${clusterSlug}`,
              },
              {
                title: `${lessonData.title || ''}`,
                link: `/aula/editar/${clusterSlug}/${id}`,
              },
              {
                title: 'Exercícios',
                link: `/aula/exercicios/${clusterSlug}/${id}`,
              },
            ]}
          />
        </HeaderSection>

        <SectionContainer>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="exercises" direction="horizontal">
              {provided => (
                <ExerciseNavigation
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {exercises.map((exercise, index) => (
                    <Draggable
                      key={exercise.id || String(index)}
                      draggableId={exercise.id || String(index)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <CardQuestion
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          current={index === currentIndex}
                          isDragging={snapshot.isDragging}
                          onClick={() => handleNavigateToExercise(index)}
                        >
                          <p>{index + 1}</p>
                        </CardQuestion>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  <AddExerciseCard onClick={handleAddExercise}>
                    <Plus size={18} weight="bold" />
                  </AddExerciseCard>
                </ExerciseNavigation>
              )}
            </Droppable>
          </DragDropContext>

          {exercises.length === 0 || formKey === 0 ? (
            <EmptyState>
              <p>Nenhum exercício cadastrado para esta aula.</p>
              <Button type="button" color="primary" onClick={handleAddExercise}>
                <Plus size={18} weight="bold" />
                Adicionar primeiro exercício
              </Button>
            </EmptyState>
          ) : (
            <form key={formKey} onSubmit={onSubmit}>
              <LineInfoQuestions>
                <div>
                  <Input
                    register={register}
                    name="exam"
                    type="text"
                    label="Instituição"
                    autoComplete="off"
                    onChange={markDirty}
                  />
                  <MessageErrorValidator>
                    {errors?.exam?.message as string}
                  </MessageErrorValidator>
                </div>
                <div>
                  <Input
                    register={register}
                    name="year"
                    type="text"
                    label="Ano da questão"
                    autoComplete="off"
                    minLength={4}
                    maxLength={4}
                    onChange={markDirty}
                  />
                  <MessageErrorValidator>
                    {errors?.year?.message as string}
                  </MessageErrorValidator>
                </div>
                <div>
                  <Controller
                    name="difficulty"
                    control={control}
                    render={({ field }) => (
                      <SimpleMultiSelect
                        register={register}
                        name={field.name}
                        label="Dificuldade"
                        placeHolder="Selecione..."
                        options={[
                          { value: 'easy', label: 'Fácil' },
                          { value: 'medium', label: 'Médio' },
                          { value: 'hard', label: 'Difícil' },
                        ]}
                        setResult={handleDifficultyChange}
                        defaultValue={field.value}
                        isMulti={false}
                        required
                      />
                    )}
                  />
                  <MessageErrorValidator>
                    {errors?.difficulty?.message as string}
                  </MessageErrorValidator>
                </div>
              </LineInfoQuestions>

              <LineField>
                <div>
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <WysiwygEditor
                        setResult={handleContentChange}
                        minEditorHeight={200}
                        uploadRoute="/exercises/upload"
                        defaultValue={field.value}
                        name={field.name}
                      />
                    )}
                  />
                  <MessageErrorValidator>
                    {errors?.content?.message as string}
                  </MessageErrorValidator>
                </div>
              </LineField>

              <DragDropContext onDragEnd={onOptionDragEnd}>
                <Droppable droppableId="options" direction="vertical">
                  {provided => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {[
                        'Alternativa A',
                        'Alternativa B',
                        'Alternativa C',
                        'Alternativa D',
                        'Alternativa E',
                      ].map((label, optIndex) => (
                        <Draggable
                          key={optIndex}
                          draggableId={`option-${optIndex}`}
                          index={optIndex}
                        >
                          {(provided, snapshot) => (
                            <LineField
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.85 : 1,
                              }}
                            >
                              <div>
                                <h4>
                                  <span
                                    {...provided.dragHandleProps}
                                    style={{
                                      cursor: 'grab',
                                      display: 'inline-flex',
                                      marginRight: '0.5rem',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    <DotsSixVertical
                                      weight="duotone"
                                      size={16}
                                    />
                                  </span>
                                  {label}
                                </h4>
                                <Controller
                                  name={`options[${optIndex}][content]` as any}
                                  control={control}
                                  render={({ field }) => (
                                    <WysiwygEditor
                                      setResult={optionChangeHandlers[optIndex]}
                                      minEditorHeight={150}
                                      uploadRoute="/exercises/upload"
                                      defaultValue={field.value}
                                      name={field.name}
                                    />
                                  )}
                                />
                                <MessageErrorValidator>
                                  {(errors?.options as any)?.length > 0 &&
                                    ((errors?.options as any)[optIndex]?.content
                                      ?.message as string)}
                                </MessageErrorValidator>
                              </div>
                            </LineField>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <LineCorrectOption>
                <main>
                  Qual é a alternativa correta?
                  <Controller
                    name="correctOption"
                    control={control}
                    render={({ field }) => (
                      <SimpleMultiSelect
                        register={register}
                        name={field.name}
                        label="Alternativa correta"
                        placeHolder="Selecione..."
                        options={[
                          { value: '0', label: 'A' },
                          { value: '1', label: 'B' },
                          { value: '2', label: 'C' },
                          { value: '3', label: 'D' },
                          { value: '4', label: 'E' },
                        ]}
                        setResult={handleCorrectOptionChange}
                        defaultValue={field.value}
                        isMulti={false}
                        required
                      />
                    )}
                  />
                  <MessageErrorValidator>
                    {errors?.correctOption?.message as string}
                  </MessageErrorValidator>
                </main>
              </LineCorrectOption>

              <Separator>
                <h2>Resolução da questão (opcional)</h2>
              </Separator>
              <LineField>
                <div>
                  <Controller
                    name="resolution"
                    control={control}
                    render={({ field }) => (
                      <WysiwygEditor
                        setResult={handleResolutionChange}
                        minEditorHeight={200}
                        uploadRoute="/exercises/upload"
                        defaultValue={field.value}
                        name={field.name}
                      />
                    )}
                  />
                  <MessageErrorValidator>
                    {errors?.resolution?.message as string}
                  </MessageErrorValidator>
                </div>
              </LineField>

              <LineButtons>
                <div>
                  {exercises.length > 1 && (
                    <Button
                      type="button"
                      color="error"
                      onClick={handleRemoveExercise}
                    >
                      <Trash size={18} weight="bold" />
                      Remover exercício {currentIndex + 1}
                    </Button>
                  )}
                </div>
                <Button type="submit" loading={saving} color="primary">
                  {hasChanges ? 'Salvar alterações' : 'Salvo'}
                </Button>
              </LineButtons>
            </form>
          )}
        </SectionContainer>
      </Container>
    </>
  );
};

export default ExerciciosEdit;
